import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@material-ui/core';
import { BackArrow, FormSkeleton } from 'components/shared';
import { useHistory } from 'react-router-dom';
import { useStyles } from './styles';
import i18n from 'i18n-js';
import { Formik, FormikHelpers } from 'formik';
import createGroupValidationSchema from './createGroupValidationSchema';

import { IdName } from '../restaurantsPage/components/NewRestaurantDialog';
import agent from 'api/agent';
import globalStore from 'store/globalStore';
import { CreateRestaurantGroupModel } from 'api/models';
import { useDispatch } from 'react-redux';
import NewGroupModal from './components/NewGroupModal';
import { useFirstLoad } from 'hooks/useFirstLoad';
import GroupFormFields, { CreateGroupForm, MenuOption } from './components/GroupFormFields';

export default function CreateRestaurantsGroupPage() {
    const classes = useStyles();
    const history = useHistory();
    const [restaurants, setRestaurants] = useState<IdName[]>([]);
    const [isRestaurantsLoading, settleRestaurants] = useFirstLoad();
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleBackButtonClick = () => {
        history.goBack();
    };

    const handleCancel = () => {
        handleBackButtonClick();
    };

    const handleCreateGroup = (
        values: CreateGroupForm,
        formikHelpers: FormikHelpers<CreateGroupForm>
    ) => {
        const createModel: CreateRestaurantGroupModel = {
            name: values.name.trim(),
            restaurants: values.selectedRestaurants.map((r) => r.id),
            managers: values.selectedUsers.map((u) => u.id),
            menusIds: values.selectedMenus.map((m) => m.id),
            catalogName: values.selectedMenus.length === 0 ? values.catalogName?.trim() : undefined,
        };

        agent.Restaurants.CreateRestaurantGroup(createModel)
            .then(() => {
                setIsModalOpen(true);
            })
            .catch((e) => {
                dispatch(globalStore.actionCreators.showToaster('error', e));
            })
            .finally(() => {
                formikHelpers.setSubmitting(false);
            });
    };

    function prepareFormValues(): CreateGroupForm {
        return {
            name: '',
            selectedRestaurants: [],
            selectedUsers: [],
            menuOption: MenuOption.Empty,
            selectedMenus: [],
        };
    }

    useEffect(() => {
        agent.Restaurants.GetSimpleList()
            .then((response) => {
                setRestaurants(response);
            })
            .catch((e) => {
                globalStore.actionCreators.showToaster('error', e);
            })
            .finally(settleRestaurants);
    }, []);

    return (
        <>
            <Formik
                enableReinitialize={true}
                initialValues={prepareFormValues()}
                validationSchema={createGroupValidationSchema()}
                validateOnMount
                onSubmit={handleCreateGroup}
            >
                {({ isValid, submitForm }) => (
                    <>
                        <div className={classes.headerContainer}>
                            <div>
                                <Box display="flex" alignItems="center" justifyContent="flex-start">
                                    <BackArrow onClick={handleBackButtonClick} />
                                    <Typography className={classes.mLeft15}>
                                        {i18n.t('restaurant.backToRestaurants')}
                                    </Typography>
                                </Box>
                            </div>
                            <div>
                                <Button variant="outlined" color="primary" onClick={handleCancel}>
                                    {i18n.t('button.cancel')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.mLeft15}
                                    onClick={submitForm}
                                    disabled={!isValid}
                                >
                                    {i18n.t('button.createGroup')}
                                </Button>
                            </div>
                        </div>
                        <div className={classes.pageBodyContainer} style={{ position: 'relative' }}>
                            <Box className={classes.mBottom15}>
                                <Typography variant="h6">
                                    {i18n.t('restaurant.createGroupPageTitle')}
                                </Typography>
                            </Box>
                            <GroupFormFields restaurants={restaurants} />
                            <FormSkeleton isLoading={isRestaurantsLoading} title columns={[3, 3]} />
                        </div>
                    </>
                )}
            </Formik>
            <NewGroupModal
                open={isModalOpen}
                onClose={handleCancel}
                title={i18n.t('restaurant.restaurantGroupCreated')}
                message={i18n.t('restaurant.restaurantGroupCreatedDescription')}
                confirmText={i18n.t('button.ok')}
            />
        </>
    );
}
